import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertCircle, ChevronRight, Clock, DollarSign, Loader2, Mail, RefreshCw, Search } from "lucide-react";
import { format } from "date-fns";
import { API } from "../../App";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const views = [
  { id: "needs_attention", label: "Needs attention" },
  { id: "overdue", label: "Overdue" },
  { id: "due_soon", label: "Due in 7 days" },
  { id: "all_open", label: "All open" },
];

const money = (value) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value || 0);

export default function CollectionsWorkspace({ token, peopleRouteBase = "/admin" }) {
  const navigate = useNavigate();
  const [view, setView] = useState("needs_attention");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/org-invoices/collections`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { view, search: query || undefined, page_size: 50 },
      });
      setData(response.data);
    } catch (requestError) {
      console.error("Error loading collections queue:", requestError);
      setError("The collections queue could not be loaded. No financial records were changed.");
    } finally {
      setLoading(false);
    }
  }, [query, token, view]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const aging = summary.aging || {};

  return (
    <section className="space-y-5" aria-labelledby="collections-heading" data-testid="collections-workspace">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-700">Accounts receivable</p>
          <h2 id="collections-heading" className="mt-1 text-2xl font-semibold text-slate-950">Collections</h2>
          <p className="mt-1 text-sm text-slate-500">Prioritize household balances that need follow-up.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="rounded-xl">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-2xl bg-slate-950 p-5 text-white sm:col-span-2 xl:col-span-1">
          <p className="text-sm text-slate-300">Open balance</p>
          <p className="mt-1 text-3xl font-semibold">{money(summary.open_balance)}</p>
          <p className="mt-2 text-xs text-slate-400">{summary.household_count || 0} households · {summary.invoice_count || 0} invoices</p>
        </div>
        {[
          ["Current", aging.current, "text-slate-700"],
          ["1–29 days", aging["1_29"], "text-amber-700"],
          ["30–59 days", aging["30_59"], "text-orange-700"],
          ["60+ days", (aging["60_89"] || 0) + (aging["90_plus"] || 0), "text-rose-700"],
        ].map(([label, amount, tone]) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-sm text-slate-500">{label}</p>
            <p className={`mt-1 text-2xl font-semibold ${tone}`}>{money(amount)}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="flex gap-1 overflow-x-auto" aria-label="Collections views">
            {views.map((item) => (
              <button key={item.id} type="button" onClick={() => setView(item.id)}
                className={`min-w-fit rounded-xl px-3 py-2 text-sm font-medium ${view === item.id ? "bg-teal-50 text-teal-800" : "text-slate-600 hover:bg-slate-50"}`}>
                {item.label}
              </button>
            ))}
          </div>
          <div className="relative lg:ml-auto lg:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input aria-label="Search collections" value={search} onChange={(event) => setSearch(event.target.value)}
              placeholder="Household, email, or invoice" className="rounded-xl pl-9" />
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6" role="alert">
          <div className="flex items-start gap-3"><AlertCircle className="mt-0.5 h-5 w-5 text-rose-600" />
            <div><h3 className="font-semibold text-rose-950">Collections unavailable</h3><p className="mt-1 text-sm text-rose-800">{error}</p>
              <Button variant="outline" onClick={load} className="mt-4 rounded-xl border-rose-200 bg-white">Try again</Button></div>
          </div>
        </div>
      ) : loading ? (
        <div className="flex min-h-56 items-center justify-center rounded-2xl border border-slate-200 bg-white" aria-label="Loading collections">
          <Loader2 className="h-8 w-8 animate-spin text-teal-700" />
        </div>
      ) : !data?.items?.length ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
          <DollarSign className="mx-auto h-10 w-10 text-slate-300" /><h3 className="mt-3 font-semibold text-slate-900">No balances in this view</h3>
          <p className="mt-1 text-sm text-slate-500">Try another saved view or clear the search.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-slate-50"><tr>
              {['Household', 'Invoice', 'Due', 'Balance', 'Status', ''].map((heading) => <th key={heading} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">{heading}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">{data.items.map((item) => (
              <tr key={item.invoice_id} className="hover:bg-slate-50">
                <td className="px-4 py-4"><p className="font-medium text-slate-950">{item.household_name}</p>
                  {item.contact_email && <a href={`mailto:${item.contact_email}`} className="mt-1 inline-flex items-center gap-1 text-xs text-teal-700"><Mail className="h-3 w-3" />{item.contact_email}</a>}</td>
                <td className="px-4 py-4"><p className="text-sm font-medium text-slate-800">#{item.invoice_number}</p><p className="max-w-60 truncate text-xs text-slate-500">{item.description}</p></td>
                <td className="px-4 py-4 text-sm text-slate-600">{item.due_date ? format(new Date(item.due_date), "MMM d, yyyy") : "No due date"}</td>
                <td className="px-4 py-4 font-semibold text-slate-950">{money(item.balance_due)}</td>
                <td className="px-4 py-4">{item.days_overdue > 0 ? <Badge className="bg-rose-50 text-rose-700">{item.days_overdue} days overdue</Badge> : item.is_due_soon ? <Badge className="bg-amber-50 text-amber-700"><Clock className="mr-1 h-3 w-3" />Due soon</Badge> : <Badge className="bg-slate-100 text-slate-700">Open</Badge>}</td>
                <td className="px-4 py-4 text-right"><Button variant="ghost" size="sm" className="rounded-xl" onClick={() => navigate(`${peopleRouteBase}/households/${item.household_id}`)} aria-label={`Open ${item.household_name}`}><ChevronRight className="h-4 w-4" /></Button></td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      )}
    </section>
  );
}
